import React, { useMemo, useState } from "react"

import { format, getDay } from "date-fns"
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  GripVertical,
  MapPin,
  Package,
  X,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { timeToMinutes } from "@/lib/collectionCoverage"
import { formatDayLong, formatWeekday } from "@/lib/dateFormats"
import { cn } from "@/lib/utils"
import { selectUnscheduledSlots } from "@/lib/slotCoverage"
import { AvailabilitySlot, DayOfWeek, Location, PlannedRoute } from "@/types/collections"
import { getSlotTypeBlockClasses, getSlotTypeEmoji } from "@/lib/slotTypeDisplay"

export interface UnscheduledSlotItem {
  slot: AvailabilitySlot
  location: Location
  customer?: { id: string; name: string; account_id: string }
}

export interface MisscheduledStopItem {
  routeId: string
  routeCode: string
  stopId: string
  stopName: string
  location: Location
  slot: AvailabilitySlot
  plannedArrival: Date
  plannedDeparture: Date
  slotStartTime: string
  slotEndTime: string
  issue: "too_early" | "too_late" | "partial_overlap"
  volumeForecast?: number
}

export interface ScheduledSlotItem {
  slot: AvailabilitySlot
  location: Location
  routeId: string
  routeCode: string
  stopId: string
  plannedArrival: Date
  plannedDeparture: Date
  volumeForecast?: number
  customer?: { id: string; name: string; account_id: string }
}

interface UnscheduledSlotsPanelProps {
  isOpen: boolean
  onClose: () => void
  selectedDate: Date
  slots: AvailabilitySlot[]
  locations: Location[]
  // Every route the date runs, not just the search-filtered ones.
  routes: PlannedRoute[]
  onDragStart?: (e: React.DragEvent, slot: UnscheduledSlotItem) => void
  onHighlightRoute?: (routeId: string) => void
}

const getDayOfWeek = (date: Date): DayOfWeek => {
  const dayIndex = getDay(date)
  const dayMap: Record<number, DayOfWeek> = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
  }
  return dayMap[dayIndex]
}



const UnscheduledSlotsPanel: React.FC<UnscheduledSlotsPanelProps> = ({
  isOpen,
  onClose,
  selectedDate,
  slots,
  locations,
  routes,
  onDragStart,
  onHighlightRoute,
}) => {
  const [showScheduledSlots, setShowScheduledSlots] = useState(false)
  const [showMisscheduled, setShowMisscheduled] = useState(true)
  const [showUnscheduled, setShowUnscheduled] = useState(true)

  const currentDayOfWeek = getDayOfWeek(selectedDate)

  const locationMap = useMemo(() => {
    const map = new Map<string, Location>()
    for (const loc of locations) {
      map.set(loc.id, loc)
    }
    return map
  }, [locations])

  // Agreed for this day with nothing planned at the location; shared with the button's badge.
  const unscheduledSlots = useMemo(() => {
    const result: UnscheduledSlotItem[] = []

    for (const slot of selectUnscheduledSlots(slots, routes, currentDayOfWeek)) {
      const location = locationMap.get(slot.location_id)
      if (!location) continue

      result.push({
        slot,
        location,
        customer: location.customer
          ? {
              id: location.customer.id,
              name: location.customer.name,
              account_id: location.customer.account_id,
            }
          : undefined,
      })
    }

    return result.sort((a, b) => {
      const customerCompare = (a.customer?.name || "").localeCompare(b.customer?.name || "")
      if (customerCompare !== 0) return customerCompare
      return a.location.site_name.localeCompare(b.location.site_name)
    })
  }, [slots, routes, currentDayOfWeek, locationMap])

  const misscheduledStops = useMemo(() => {
    const result: MisscheduledStopItem[] = []

    for (const route of routes) {
      for (const stop of route.stops) {
        if (!stop.locationId) continue

        const matchingSlot = slots.find(
          (slot) =>
            slot.location_id === stop.locationId &&
            slot.is_active &&
            slot.days?.includes(currentDayOfWeek),
        )

        if (!matchingSlot) continue

        const location = locationMap.get(stop.locationId)
        if (!location) continue

        const stopArrivalMinutes =
          stop.plannedArrival.getHours() * 60 + stop.plannedArrival.getMinutes()
        const stopDepartureMinutes =
          stop.plannedDeparture.getHours() * 60 + stop.plannedDeparture.getMinutes()
        const slotStartMinutes = timeToMinutes(matchingSlot.start_time)
        const slotEndMinutes = timeToMinutes(matchingSlot.end_time)

        let issue: "too_early" | "too_late" | "partial_overlap" | null = null

        // Null means unparseable; compared, it would coerce to midnight and flag every stop late.
        if (slotStartMinutes === null || slotEndMinutes === null) {
          issue = null
        } else if (stopDepartureMinutes < slotStartMinutes) {
          issue = "too_early"
        } else if (stopArrivalMinutes > slotEndMinutes) {
          issue = "too_late"
        } else if (stopArrivalMinutes < slotStartMinutes || stopDepartureMinutes > slotEndMinutes) {
          issue = "partial_overlap"
        }

        if (issue) {
          result.push({
            routeId: route.id,
            routeCode: route.code,
            stopId: stop.id,
            stopName: stop.name || location.site_name,
            location,
            slot: matchingSlot,
            plannedArrival: stop.plannedArrival,
            plannedDeparture: stop.plannedDeparture,
            slotStartTime: matchingSlot.start_time,
            slotEndTime: matchingSlot.end_time,
            issue,
            volumeForecast: stop.volumeEstimated,
          })
        }
      }
    }

    return result
  }, [routes, slots, currentDayOfWeek, locationMap])

  const scheduledSlots = useMemo(() => {
    const result: ScheduledSlotItem[] = []

    for (const route of routes) {
      for (const stop of route.stops) {
        if (!stop.locationId) continue

        const matchingSlot = slots.find(
          (slot) =>
            slot.location_id === stop.locationId &&
            slot.is_active &&
            slot.days?.includes(currentDayOfWeek),
        )

        if (!matchingSlot) continue

        const location = locationMap.get(stop.locationId)
        if (!location) continue

        result.push({
          slot: matchingSlot,
          location,
          routeId: route.id,
          routeCode: route.code,
          stopId: stop.id,
          plannedArrival: stop.plannedArrival,
          plannedDeparture: stop.plannedDeparture,
          volumeForecast: stop.volumeEstimated,
          customer: location.customer
            ? {
                id: location.customer.id,
                name: location.customer.name,
                account_id: location.customer.account_id,
              }
            : undefined,
        })
      }
    }

    return result.sort((a, b) => {
      const customerCompare = (a.customer?.name || "").localeCompare(b.customer?.name || "")
      if (customerCompare !== 0) return customerCompare
      return a.location.site_name.localeCompare(b.location.site_name)
    })
  }, [routes, slots, currentDayOfWeek, locationMap])

  const groupedScheduledSlots = useMemo(() => {
    const groups = new Map<string, ScheduledSlotItem[]>()

    for (const item of scheduledSlots) {
      const customerKey = item.customer?.name || "No Customer"
      const existing = groups.get(customerKey) || []
      existing.push(item)
      groups.set(customerKey, existing)
    }

    return groups
  }, [scheduledSlots])

  const groupedUnscheduledSlots = useMemo(() => {
    const groups = new Map<string, UnscheduledSlotItem[]>()

    for (const item of unscheduledSlots) {
      const customerKey = item.customer?.name || "No Customer"
      const existing = groups.get(customerKey) || []
      existing.push(item)
      groups.set(customerKey, existing)
    }

    return groups
  }, [unscheduledSlots])

  // Counts only unplanned slots; a misscheduled stop is already on a route.
  const notScheduledCount = unscheduledSlots.length
  const totalMisscheduledVolume = misscheduledStops.reduce(
    (sum, item) => sum + (item.volumeForecast || 0),
    0,
  )

  const handleDragStart = (e: React.DragEvent, item: UnscheduledSlotItem) => {
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        type: "unscheduled-slot",
        slotId: item.slot.id,
        locationId: item.location.id,
        locationName: item.location.site_name,
        slotType: item.slot.slot_type,
        slotServices: item.slot.slot_services,
        slotPnl: item.slot.slot_pnl,
        startTime: item.slot.start_time,
        endTime: item.slot.end_time,
        serviceTimeMinutes: item.slot.service_time_minutes,
      }),
    )
    e.dataTransfer.effectAllowed = "copy"

    if (onDragStart) {
      onDragStart(e, item)
    }
  }

  if (!isOpen) return null

  return (
    <div className="w-80 flex-shrink-0 bg-white border-l border-palette-jungleGreen200 flex flex-col h-full shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-palette-jungleGreen200 bg-palette-jungleGreen000">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-palette-jungleGreen900">Unscheduled Slots</h3>
          {notScheduledCount > 0 && (
            <Badge variant="destructive" className="h-5 px-1.5 text-xs">
              {notScheduledCount}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X size={14} />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          <div className="text-xs text-palette-jungleGreen500 flex items-center gap-1.5">
            <Clock size={12} />
            <span>{formatDayLong(selectedDate)}</span>
          </div>

          {misscheduledStops.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowMisscheduled(!showMisscheduled)}
                aria-expanded={showMisscheduled}
                className="w-full flex items-center justify-between gap-2 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {showMisscheduled ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <AlertTriangle size={12} />
                  <span>Scheduled Outside Window ({misscheduledStops.length})</span>
                </div>
                {totalMisscheduledVolume > 0 && (
                  <div className="flex items-center gap-1 text-[10px] text-amber-600">
                    <Package size={10} />
                    <span>{totalMisscheduledVolume.toLocaleString()}</span>
                  </div>
                )}
              </button>

              {showMisscheduled && (
                <div className="space-y-1.5">
                  {misscheduledStops.map((item) => (
                    // A div, not a button: the row contains a tooltip trigger button.
                    <div
                      key={`${item.routeId}-${item.stopId}`}
                      role={onHighlightRoute ? "button" : undefined}
                      tabIndex={onHighlightRoute ? 0 : undefined}
                      onClick={() => onHighlightRoute?.(item.routeId)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          onHighlightRoute?.(item.routeId)
                        }
                      }}
                      title={onHighlightRoute ? `Show ${item.routeCode} on the timeline` : undefined}
                      className={cn(
                        "p-2 rounded-md border border-amber-200 bg-amber-50",
                        onHighlightRoute &&
                          "cursor-pointer transition-colors hover:bg-amber-100 hover:border-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-palette-jungleGreen900 truncate">
                            {item.stopName}
                          </div>
                          <div className="text-xs text-palette-jungleGreen500 truncate">
                            Route: {item.routeCode}
                          </div>
                        </div>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px] shrink-0",
                                item.issue === "too_early" && "border-blue-300 text-blue-700",
                                item.issue === "too_late" && "border-red-300 text-red-700",
                                item.issue === "partial_overlap" && "border-amber-300 text-amber-700",
                              )}
                            >
                              {item.issue === "too_early" && "Early"}
                              {item.issue === "too_late" && "Late"}
                              {item.issue === "partial_overlap" && "Overlap"}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <div className="text-xs">
                              <div>
                                Scheduled: {format(item.plannedArrival, "HH:mm")} -{" "}
                                {format(item.plannedDeparture, "HH:mm")}
                              </div>
                              <div>
                                Slot window: {item.slotStartTime} - {item.slotEndTime}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[10px] text-palette-jungleGreen500">
                        <span>
                          ⏰ {format(item.plannedArrival, "HH:mm")}-
                          {format(item.plannedDeparture, "HH:mm")}
                        </span>
                        <span className="text-amber-600">→</span>
                        <span className="text-amber-700">
                          {item.slotStartTime}-{item.slotEndTime}
                        </span>
                        {item.volumeForecast !== undefined && item.volumeForecast > 0 && (
                          <>
                            <span className="text-palette-jungleGreen300">•</span>
                            <span className="flex items-center gap-0.5">
                              <Package size={9} />
                              {item.volumeForecast.toLocaleString()}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {scheduledSlots.length > 0 && (
            <div className="space-y-2">
              <button
                onClick={() => setShowScheduledSlots(!showScheduledSlots)}
                className="w-full flex items-center justify-between text-xs font-medium text-palette-jungleGreen700 hover:text-palette-jungleGreen900 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={12} className="text-green-600" />
                  <span>Scheduled ({scheduledSlots.length})</span>
                </div>
                {showScheduledSlots ? (
                  <ChevronDown size={12} className="text-palette-jungleGreen400" />
                ) : (
                  <ChevronRight size={12} className="text-palette-jungleGreen400" />
                )}
              </button>

              {showScheduledSlots && (
                <div className="space-y-3 pl-4 border-l-2 border-green-200">
                  {Array.from(groupedScheduledSlots.entries()).map(([customerName, items]) => (
                    <div key={customerName} className="space-y-1.5">
                      <div className="text-xs font-medium text-palette-jungleGreen600 flex items-center gap-1">
                        <ChevronRight size={10} />
                        {customerName}
                      </div>

                      {items.map((item) => (
                        <div
                          key={`${item.routeId}-${item.stopId}`}
                          className={cn(
                            "p-2 rounded-md border-l-2 transition-all",
                            getSlotTypeBlockClasses(item.slot.slot_type),
                            "opacity-75",
                          )}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm">
                                  {getSlotTypeEmoji(item.slot.slot_type)}
                                </span>
                                <span className="text-xs font-medium text-palette-jungleGreen900 truncate">
                                  {item.location.site_name}
                                </span>
                              </div>
                              <div className="mt-0.5 flex items-center gap-2 text-[10px] text-palette-jungleGreen500">
                                <span className="flex items-center gap-0.5">
                                  <Clock size={9} />
                                  {format(item.plannedArrival, "HH:mm")} -{" "}
                                  {format(item.plannedDeparture, "HH:mm")}
                                </span>
                                <span className="text-palette-jungleGreen300">•</span>
                                <span className="text-palette-jungleGreen600 font-medium">
                                  Route: {item.routeCode}
                                </span>
                              </div>
                              {item.slot.slot_services && item.slot.slot_services.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {item.slot.slot_services.map((service) => (
                                    <Badge
                                      key={service}
                                      variant="secondary"
                                      className="text-[9px] h-4 px-1"
                                    >
                                      {service}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {item.volumeForecast !== undefined && item.volumeForecast > 0 && (
                                <div className="mt-1 flex items-center gap-1 text-[9px] text-palette-jungleGreen500">
                                  <Package size={8} />
                                  <span>{item.volumeForecast.toLocaleString()} items</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {unscheduledSlots.length > 0 ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setShowUnscheduled(!showUnscheduled)}
                aria-expanded={showUnscheduled}
                className="w-full flex items-center gap-2 text-xs font-medium text-palette-jungleGreen700 hover:text-palette-jungleGreen900 transition-colors"
              >
                {showUnscheduled ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Package size={12} />
                <span>Not Scheduled ({unscheduledSlots.length})</span>
              </button>

              {showUnscheduled &&
                Array.from(groupedUnscheduledSlots.entries()).map(([customerName, items]) => (
                  <div key={customerName} className="space-y-1.5">
                    <div className="text-xs font-medium text-palette-jungleGreen600 flex items-center gap-1">
                      <ChevronRight size={10} />
                      {customerName}
                    </div>

                    {items.map((item) => (
                      <div
                        key={item.slot.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        className={cn(
                          "p-2 rounded-md border-l-2 cursor-grab active:cursor-grabbing transition-all hover:shadow-sm",
                          getSlotTypeBlockClasses(item.slot.slot_type),
                          "hover:ring-1 hover:ring-blue-300",
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 text-palette-jungleGreen400">
                            <GripVertical size={12} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm">{getSlotTypeEmoji(item.slot.slot_type)}</span>
                              <span className="text-xs font-medium text-palette-jungleGreen900 truncate">
                                {item.location.site_name}
                              </span>
                            </div>
                            <div className="mt-0.5 flex items-center gap-2 text-[10px] text-palette-jungleGreen500">
                              <span className="flex items-center gap-0.5">
                                <Clock size={9} />
                                {item.slot.start_time} - {item.slot.end_time}
                              </span>
                              <span className="flex items-center gap-0.5">
                                <MapPin size={9} />
                                {item.location.postcode}
                              </span>
                            </div>
                            {item.slot.slot_services && item.slot.slot_services.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {item.slot.slot_services.map((service) => (
                                  <Badge
                                    key={service}
                                    variant="secondary"
                                    className="text-[9px] h-4 px-1"
                                  >
                                    {service}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <div className="mt-1 flex items-center gap-1 text-[9px] text-palette-jungleGreen400">
                              <Package size={8} />
                              <span>Volume forecast applied when scheduled</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          ) : misscheduledStops.length === 0 && scheduledSlots.length === 0 ? (
            <div className="text-center py-8 text-palette-jungleGreen500">
              <Package size={32} className="mx-auto mb-2 text-palette-jungleGreen300" />
              <p className="text-sm">No slots for this date</p>
              <p className="text-xs mt-1">No slots active for {formatWeekday(selectedDate)}</p>
            </div>
          ) : misscheduledStops.length === 0 &&
            scheduledSlots.length > 0 &&
            unscheduledSlots.length === 0 ? (
            <div className="text-center py-8 text-palette-jungleGreen500">
              <CheckCircle2 size={32} className="mx-auto mb-2 text-green-300" />
              <p className="text-sm">All slots are scheduled</p>
              <p className="text-xs mt-1">No issues for {formatWeekday(selectedDate)}</p>
            </div>
          ) : null}
        </div>
      </ScrollArea>

      <div className="px-3 py-2 border-t border-palette-jungleGreen100 bg-palette-jungleGreen000">
        <p className="text-[10px] text-palette-jungleGreen500 text-center">
          Drag slots to the timeline to add them to a route
        </p>
      </div>
    </div>
  )
}

export default UnscheduledSlotsPanel
